/**
 * «Работы хозяйства и карта» — виджет страницы агроцикла («Работы агроцикла и
 * карта», решение владельца 08.10), суженный до ОДНОГО хозяйства:
 *
 * - сверху — план агроцикла компактной шкалой этапов (окна — план сервера
 *   `/agrocycles/{key}`, доля — ход работ ЭТОГО хозяйства); нажатие на этап
 *   оставляет в списке только его работы;
 * - слева — работы хозяйства в агроцикле (его обязательства из
 *   `/season/picture?actor_id`): что выполнено, сколько га работ, просрочка;
 *   порядок — активные сверху, давно закрытые внизу (`farmWorks.ts`);
 * - справа — карта ТЕРРИТОРИИ хозяйства в агроцикле (его контуры целиком или
 *   частями, `/agrocycles/{key}/territory?actor_id`); выбор работы
 *   перекрашивает кадр в статусы её работ на его полях
 *   (`/season/contour-status?campaign_id&actor_id`).
 *
 * В развёрнутой карте список работ — её боковая панель (тот же выбор).
 */
import { useMemo, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { Map as MapIcon, X } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Icon } from '../../ui/Icon';
import { EmptyState } from '../../ui/EmptyState';
import { LoadError, failed } from '../../ui/LoadError';
import { SkeletonTable } from '../../ui/Skeleton';
import { MapWidget } from '../../map/MapWidget';
import { workStatusByContour, type MapLayerData } from '../../map/layers';
import { api, request, type QueryParams, type Schemas } from '../../../api/client';
import { useRequest } from '../../../api/useRequest';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { countOf, dateShort, haUnit, num, pctAxis, windowLabel, windowShort, WORDS } from '../../lib/format';
import { haOf, hasArea } from '../../lib/areaWork';
import { toWork } from '../../data-source/api/works';
import { agrocycleApi } from '../agrocycle/agrocycleApi';
import { assignLanes, buildStages, makeAxis, type StageCard } from '../cycle/cyclePlanModel';
import { doneSplit, workHref, workPhase, workTitle, WORK_PHASE_LABEL, WORK_PHASE_ORDER, type CampaignWork, type WorkPhase } from './model';
import { sortFarmWorks } from './farmWorks';
import './farmWorks.css';

const m = defineMessages({
  title: { id: 'farm.works.title', defaultMessage: '{cycle}: работы хозяйства и карта', description: 'Заголовок виджета вкладки «Агроциклы и работы»: работы хозяйства слева, карта его территории справа; {cycle} — «Хлопок 2026»' },
  aside: { id: 'farm.works.aside', defaultMessage: '{works} · после срока {overdue}', description: 'Сводка в шапке виджета работ хозяйства; {works} — «22 работы», {overdue} — число работ после срока строкой' },
  planLabel: { id: 'farm.works.plan.label', defaultMessage: 'План агроцикла', description: 'Подпись над компактной шкалой этапов плана агроцикла в виджете работ хозяйства' },
  planNote: { id: 'farm.works.plan.note', defaultMessage: 'доля — ход работ этого хозяйства; нажмите этап, чтобы оставить в списке его работы', description: 'Пояснение рядом с подписью шкалы этапов: что за доля на полосе и что даёт нажатие; строчными' },
  planAria: { id: 'farm.works.plan.aria', defaultMessage: 'Этапы плана агроцикла: выбор оставляет в списке работы этапа', description: 'aria-label шкалы этапов плана в виджете работ хозяйства' },
  stageTitle: { id: 'farm.works.plan.stageTitle', defaultMessage: '{name}: {window}', description: 'Всплывающая подсказка полосы этапа; {name} — этап, {window} — даты окна' },
  today: { id: 'farm.works.plan.today', defaultMessage: 'срез {date}', description: 'Метка линии даты среза на шкале этапов; {date} — дата, коротко' },
  all: { id: 'farm.works.all', defaultMessage: 'Вся территория хозяйства в агроцикле', description: 'Пункт списка работ: показать на карте все контуры хозяйства в агроцикле (без выбранной работы)' },
  allNote: { id: 'farm.works.allNote', defaultMessage: '{contours} · {land} земли по размещению', description: 'Подпись пункта «Вся территория»; {contours} — «12 контуров», {land} — «84,20 га»' },
  allLoading: { id: 'farm.works.allLoading', defaultMessage: 'территория читается…', description: 'Подпись пункта «Вся территория», пока территория хозяйства грузится' },
  phaseAria: { id: 'farm.works.phaseAria', defaultMessage: 'Состояние работ: фильтр списка', description: 'aria-label ряда кнопок-фильтров по состоянию работ в виджете работ хозяйства' },
  phaseAll: { id: 'farm.works.phaseAll', defaultMessage: 'Все', description: 'Кнопка-фильтр: все работы; коротко' },
  stageChip: { id: 'farm.works.stageChip', defaultMessage: 'этап: {name}', description: 'Чип выбранного этапа над списком работ (нажатие снимает); {name} — этап' },
  stageChipClear: { id: 'farm.works.stageChipClear', defaultMessage: 'Снять выбор этапа {name}', description: 'aria-label кнопки снятия выбранного этапа; {name} — этап' },
  listAria: { id: 'farm.works.listAria', defaultMessage: 'Работы хозяйства: выбор для карты', description: 'aria-label списка работ слева от карты' },
  rowDone: { id: 'farm.works.rowDone', defaultMessage: 'выполнено {done} из {total}', description: 'Цифры строки работы: выполнено из всего; {done}/{total} — «7,20 га» или «5 работ на полях»' },
  rowOverdue: { id: 'farm.works.rowOverdue', defaultMessage: 'после срока {value}', description: 'Хвост строки работы: без отчёта при закрытом окне; {value} — «1,20 га работ» или «3 работы на полях»' },
  rowHaWork: { id: 'farm.works.rowHaWork', defaultMessage: '{ha} работ', description: 'Гектары работ в строке работы; {ha} — «7,20 га»' },
  open: { id: 'farm.works.open', defaultMessage: 'Страница работы по хозяйству', description: 'Ссылка из выбранной работы: страница работы, суженная до хозяйства' },
  emptyTitle: { id: 'farm.works.emptyTitle', defaultMessage: 'Работ нет', description: 'Пустой список работ хозяйства в агроцикле' },
  emptyAll: { id: 'farm.works.emptyAll', defaultMessage: 'у хозяйства нет работ на полях в этом агроцикле — на карте его территория', description: 'Причина пустого списка без фильтров' },
  emptyFiltered: { id: 'farm.works.emptyFiltered', defaultMessage: 'под выбранный этап или состояние работ нет — снимите фильтр', description: 'Причина пустого списка при фильтре' },
  loadTerritory: { id: 'farm.works.loadTerritory', defaultMessage: 'территорию хозяйства в агроцикле', description: 'Что не загрузилось (винительный падеж): контуры хозяйства в агроцикле для карты' },
  loadWorks: { id: 'farm.works.loadWorks', defaultMessage: 'работы хозяйства', description: 'Что не загрузилось (винительный падеж): работы хозяйства в агроцикле' },
  layerTitle: { id: 'farm.works.layerTitle', defaultMessage: 'Территория хозяйства в агроцикле', description: 'Заголовок легенды слоя территории хозяйства' },
  coverWhole: { id: 'farm.works.cover.whole', defaultMessage: 'контур целиком', description: 'Легенда карты: контур хозяйства в агроцикле целиком' },
  coverPart: { id: 'farm.works.cover.part', defaultMessage: 'часть контура', description: 'Легенда карты: в агроцикле только часть контура хозяйства' },
  coverNone: { id: 'farm.works.cover.none', defaultMessage: 'вне агроцикла', description: 'Легенда карты: контур не в агроцикле' },
  captionTerritory: { id: 'farm.works.captionTerritory', defaultMessage: 'Контуры хозяйства в агроцикле по размещению: целиком или частью', description: 'Подпись под картой без выбранной работы' },
  captionWork: { id: 'farm.works.captionWork', defaultMessage: 'Цвет — состояние работы «{name}» на полях хозяйства', description: 'Подпись под картой при выбранной работе; {name} — работа' },
  captionLoading: { id: 'farm.works.captionLoading', defaultMessage: 'Статусы работы читаются…', description: 'Подпись под картой, пока статусы выбранной работы грузятся' },
  captionNoStatus: { id: 'farm.works.captionNoStatus', defaultMessage: 'Статус контуров по работе «{name}» сервер не отдал', description: 'Подпись под картой: статусов нет; {name} — работа' },
  note: { id: 'farm.works.note', defaultMessage: 'Числа — работы на полях этого хозяйства: «выполнено» — отчёт «выполнено»; «после срока» — отчёта нет при закрытом окне (это про данные, а не про работу в поле). Гектары — гектары работ: поле с двумя работами считается дважды.', description: 'Пояснение под виджетом работ хозяйства (хоким, менеджер)' },
});

type Territory = Schemas['CycleTerritoryOut'];

/** Наименьшая ширина полосы этапа на шкале (доля оси): подпись читается. */
const MIN_BAND = 0.1;

const PHASE_TONE: Record<WorkPhase, string> = { done: 'done', in_progress: 'progress', overdue: 'late', in_window: 'open', ahead: 'ahead' };

export function FarmWorksMap({
  cycleKey,
  cycleName,
  farmId,
  works,
  loading,
  worksError,
  asOf,
  params,
  paramsKey,
  linkSearch,
}: {
  cycleKey: string;
  cycleName: string;
  farmId: number;
  works: ReadonlyArray<CampaignWork>;
  loading: boolean;
  worksError: { error: { message?: string } | string | null; reload: () => void } | null;
  asOf: string;
  params: QueryParams;
  paramsKey: string;
  linkSearch: (extra?: Record<string, string | number>) => string;
}) {
  const t = useT();
  const [pickedId, setPickedId] = useState<number | null>(null);
  const [phase, setPhase] = useState<WorkPhase | 'all'>('all');
  const [stageKey, setStageKey] = useState<string | null>(null);

  const territory = useRequest<Territory>(
    () => request<Territory>(`/agrocycles/${encodeURIComponent(cycleKey)}/territory`, { ...params, actor_id: farmId }),
    [cycleKey, farmId, paramsKey],
  );
  const plan = useRequest(
    () => agrocycleApi.detail(cycleKey, { ...params, actor_id: farmId }).catch(() => null),
    [cycleKey, farmId, paramsKey],
  );
  const picked = works.find((w) => w.campaign_id === pickedId) ?? null;
  const status = useRequest(
    () => (picked ? api.seasonContourStatus({ ...params, campaign_id: picked.campaign_id, actor_id: farmId }) : Promise.resolve(null)),
    [picked?.campaign_id, farmId, paramsKey],
  );

  // Этапы плана с ходом работ хозяйства: адаптер канона переводит его работы
  // в вид модели шкалы (та же сводка, что на странице агроцикла).
  const stages = useMemo(() => {
    const asWorks = works.map((w, i) => toWork(w, cycleKey, i + 1, asOf));
    return buildStages(plan.data?.plan.stages, asWorks, asOf);
  }, [works, plan.data, cycleKey, asOf]);
  const axis = useMemo(() => makeAxis(stages.flatMap((s) => [s.from, s.to])), [stages]);
  const lanes = useMemo(
    () =>
      axis
        ? assignLanes(stages, (s) => {
            const left = axis.at(s.from) ?? 0;
            const right = axis.at(s.to ?? s.from) ?? 1;
            return { left, right: Math.max(right, left + MIN_BAND) + 0.004 };
          })
        : 0,
    [stages, axis],
  );
  const stage: StageCard | null = stages.find((s) => s.key === stageKey) ?? null;
  const stageIds = useMemo(
    () => (stage ? new Set(stage.items.flatMap((i) => (i.work ? [Number(i.work.id)] : []))) : null),
    [stage],
  );

  const sorted = useMemo(() => sortFarmWorks(works, asOf), [works, asOf]);
  const inStage = stageIds ? sorted.filter((w) => stageIds.has(w.campaign_id)) : sorted;
  const phases = useMemo(() => {
    const out: Record<WorkPhase, number> = { done: 0, in_progress: 0, overdue: 0, in_window: 0, ahead: 0 };
    for (const w of inStage) out[workPhase(w, asOf)] += 1;
    return out;
  }, [inStage, asOf]);
  const shown = phase === 'all' ? inStage : inStage.filter((w) => workPhase(w, asOf) === phase);
  const overdueTotal = works.filter((w) => workPhase(w, asOf) === 'overdue').length;

  const territoryIds = useMemo(() => territory.data?.contours.map((c) => String(c.contour_id)), [territory.data]);
  const territoryLayer: MapLayerData | undefined = useMemo(() => {
    if (!territory.data) return undefined;
    const byId: Record<string, string> = {};
    for (const c of territory.data.contours) byId[String(c.contour_id)] = c.cover;
    return {
      values: {
        title: t(m.layerTitle),
        byId,
        legend: [
          { key: 'whole', label: t(m.coverWhole), tone: 'ramp-4' },
          { key: 'part', label: t(m.coverPart), tone: 'ramp-2' },
        ],
        noneLabel: t(m.coverNone),
      },
    };
  }, [territory.data, t]);
  const statusById = useMemo(() => (picked && status.data ? workStatusByContour(status.data.items) : undefined), [picked, status.data]);
  const workIds = useMemo(() => (statusById ? Object.keys(statusById) : undefined), [statusById]);

  const mapProps = picked && statusById
    ? { colorBy: 'workStatus' as const, workStatusById: statusById, filterIds: workIds, focusIds: workIds }
    : territoryLayer
      ? { colorBy: 'values' as const, data: territoryLayer, filterIds: territoryIds, focusIds: territoryIds }
      : { colorBy: 'crop' as const, filterIds: territoryIds, focusIds: territoryIds };
  const caption = picked
    ? statusById
      ? t(m.captionWork, { name: workTitle(picked.name) })
      : status.state === 'loading'
        ? t(m.captionLoading)
        : t(m.captionNoStatus, { name: workTitle(picked.name) })
    : t(m.captionTerritory);

  const pickStage = (key: string) => {
    setStageKey(stageKey === key ? null : key);
    setPhase('all');
  };

  const row = (w: CampaignWork) => {
    const p = workPhase(w, asOf);
    const aw = hasArea(w.area_work) ? w.area_work! : null;
    const o = doneSplit(w.totals.obligations);
    const share = aw ? (aw.done_pct ?? 0) / 100 : o.total ? (o.done ?? o.reported) / o.total : 0;
    const done = aw
      ? t(m.rowDone, { done: haUnit(haOf(aw.done_microha)), total: t(m.rowHaWork, { ha: haUnit(haOf(aw.total_microha)) }) })
      : t(m.rowDone, { done: num(o.done ?? o.reported), total: countOf(o.total, WORDS.obligation) });
    const overdue = aw && (aw.overdue_microha ?? 0) > 0
      ? t(m.rowHaWork, { ha: haUnit(haOf(aw.overdue_microha)) })
      : !aw && (w.totals.overdue.value ?? 0) > 0
        ? countOf(w.totals.overdue.value!, WORDS.obligation)
        : null;
    const on = picked?.campaign_id === w.campaign_id;
    return (
      <li key={w.campaign_id}>
        <button
          type="button"
          className={`fwm-row focus-ring ${on ? 'is-on' : ''}`}
          data-phase={PHASE_TONE[p]}
          aria-pressed={on}
          onClick={() => setPickedId(on ? null : w.campaign_id)}
        >
          <span className="fwm-row__top">
            <strong className="fwm-row__name">{workTitle(w.name)}</strong>
            <span className="fwm-row__win num">{windowShort(w.window_from, w.window_to)}</span>
          </span>
          <span className="fwm-row__stage">
            {w.stage.name} · <span className="fwm-row__phase">{WORK_PHASE_LABEL[p]}</span>
          </span>
          <span className="fwm-bar" aria-hidden="true"><i style={{ width: `${Math.round(Math.min(1, share) * 100)}%` }} /></span>
          <span className="fwm-row__fig">
            {done}
            {aw && aw.done_pct !== null ? ` · ${pctAxis(aw.done_pct)}` : null}
            {overdue ? <span className="fwm-row__late"> · {t(m.rowOverdue, { value: overdue })}</span> : null}
          </span>
        </button>
        {on ? (
          <Link className="fwm-row__open" to={workHref(cycleKey, w.campaign_id, farmId, linkSearch)}>{t(m.open)} →</Link>
        ) : null}
      </li>
    );
  };

  const allNote = territory.data
    ? t(m.allNote, { contours: countOf(territory.data.contours.length, WORDS.contour), land: haUnit(territory.data.land_ha) })
    : t(m.allLoading);

  const list = (compact: boolean) => (
    <div className={compact ? 'fwm-list fwm-list--compact' : 'fwm-list'}>
      <button type="button" className={`fwm-all focus-ring ${picked ? '' : 'is-on'}`} aria-pressed={!picked} onClick={() => setPickedId(null)}>
        <Icon icon={MapIcon} size={16} />
        <span>
          <strong>{t(m.all)}</strong>
          <span className="t-caption">{allNote}</span>
        </span>
      </button>
      {works.length > 0 ? (
        <div className="fwm-chips" role="group" aria-label={t(m.phaseAria)}>
          {stage ? (
            <button type="button" className="cab-pick fwm-stagechip" aria-pressed="true" aria-label={t(m.stageChipClear, { name: stage.name })} onClick={() => setStageKey(null)}>
              {t(m.stageChip, { name: stage.name })} <Icon icon={X} size={16} />
            </button>
          ) : null}
          <button type="button" className="cab-pick" aria-pressed={phase === 'all'} onClick={() => setPhase('all')}>
            {t(m.phaseAll)} <small>{num(inStage.length)}</small>
          </button>
          {WORK_PHASE_ORDER.filter((p) => phases[p] > 0).map((p) => (
            <button key={p} type="button" className="cab-pick" aria-pressed={phase === p} onClick={() => setPhase(phase === p ? 'all' : p)}>
              {WORK_PHASE_LABEL[p]} <small>{num(phases[p])}</small>
            </button>
          ))}
        </div>
      ) : null}
      {worksError ? (
        <LoadError compact what={t(m.loadWorks)} error={worksError.error} onRetry={worksError.reload} />
      ) : loading ? (
        <SkeletonTable rows={4} />
      ) : shown.length === 0 ? (
        <EmptyState compact title={t(m.emptyTitle)} reason={works.length === 0 ? t(m.emptyAll) : t(m.emptyFiltered)} />
      ) : (
        <ul className="fwm-rows" aria-label={t(m.listAria)}>
          {shown.map(row)}
        </ul>
      )}
    </div>
  );

  const todayPos = axis?.at(asOf) ?? null;

  return (
    <Card
      size="lg"
      className="fwm"
      title={t(m.title, { cycle: cycleName })}
      aside={t(m.aside, { works: countOf(works.length, WORDS.work), overdue: num(overdueTotal) })}
    >
      {axis && stages.length > 0 ? (
        <div className="fwm-plan">
          <p className="fwm-plan__label t-caption">
            <strong>{t(m.planLabel)}</strong> · {t(m.planNote)}
          </p>
          <div className="fwm-plan__scroll">
            <div className="fwm-axis" role="group" aria-label={t(m.planAria)} style={{ '--lanes': Math.max(1, lanes) } as CSSProperties}>
              {axis.months.map((month) => (
                <span key={month.iso} className="fwm-month" style={{ left: `${month.pos * 100}%` }} aria-hidden="true">
                  {dateShort(month.iso).replace(/^\d+[\s-]*/, '')}
                </span>
              ))}
              {stages.filter((s) => s.lane >= 0).map((s) => {
                const left = axis.at(s.from) ?? 0;
                const right = axis.at(s.to ?? s.from) ?? 1;
                const on = s.key === stageKey;
                return (
                  <button
                    key={s.key}
                    type="button"
                    className={`fwm-band focus-ring ${on ? 'is-on' : ''}`}
                    data-state={s.state}
                    aria-pressed={on}
                    title={t(m.stageTitle, { name: s.name, window: windowLabel(s.from, s.to) })}
                    style={{ left: `${left * 100}%`, width: `${Math.max(right - left, MIN_BAND) * 100}%`, top: `calc(var(--fwm-head) + ${s.lane} * var(--fwm-lane))` }}
                    onClick={() => pickStage(s.key)}
                  >
                    <i className="fwm-band__fill" style={{ width: `${Math.round((s.done ?? 0) * 100)}%` }} aria-hidden="true" />
                    <span className="fwm-band__name">{s.name}</span>
                    {s.done !== null ? <span className="fwm-band__pct num">{pctAxis(s.done * 100)}</span> : null}
                  </button>
                );
              })}
              {todayPos !== null ? (
                <span className="fwm-today" style={{ left: `${todayPos * 100}%` }}>
                  <span>{t(m.today, { date: dateShort(asOf) })}</span>
                </span>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      {failed(territory) ? <LoadError compact what={t(m.loadTerritory)} error={territory.error} onRetry={territory.reload} /> : null}
      <div className="fwm-split">
        {list(false)}
        <div className="fwm-map">
          <MapWidget
            {...mapProps}
            mode="frame"
            height="100%"
            legend="below"
            caption={caption}
            panel={{ title: t(m.title, { cycle: cycleName }), summary: picked ? workTitle(picked.name) : t(m.all), content: list(true) }}
          />
        </div>
      </div>
      <p className="cab-why fwm-note">{t(m.note)}</p>
    </Card>
  );
}
